import { useState } from 'react';

export interface ProductPhotoOptions {
  whiteBackground: boolean;
  addShadow: boolean;
  shadowIntensity: number;
  addReflection: boolean;
  reflectionIntensity: number;
  gridLayout?: 'single' | '2x2' | '3x3' | '4x4';
  backgroundColor?: string;
}

interface ProcessedProduct {
  id: string;
  original: string;
  processed: string;
  settings: ProductPhotoOptions;
}

export const useProductPhotoTools = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processedProducts, setProcessedProducts] = useState<ProcessedProduct[]>([]);

  const processProduct = async (
    imageUrl: string,
    imageId: string,
    options: ProductPhotoOptions
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          canvas.width = img.width;
          canvas.height = img.height;

          // Apply white/custom background
          if (options.whiteBackground || options.backgroundColor) {
            ctx.fillStyle = options.backgroundColor || '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }

          // Add reflection (draw before main image)
          if (options.addReflection) {
            ctx.save();
            ctx.translate(0, canvas.height);
            ctx.scale(1, -1);
            ctx.globalAlpha = options.reflectionIntensity / 100;
            
            // Create gradient for fade effect
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height / 2);
            gradient.addColorStop(0, 'rgba(0,0,0,0)');
            gradient.addColorStop(1, 'rgba(0,0,0,1)');
            
            ctx.drawImage(img, 0, canvas.height / 2, canvas.width, canvas.height / 2);
            ctx.globalCompositeOperation = 'destination-in';
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height / 2);
            ctx.restore();
          }

          // Draw main image
          ctx.drawImage(img, 0, 0);

          // Add shadow
          if (options.addShadow) {
            ctx.save();
            ctx.globalCompositeOperation = 'destination-over';
            
            const shadowBlur = (options.shadowIntensity / 100) * 50;
            const shadowOffset = (options.shadowIntensity / 100) * 20;
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = shadowBlur;
            ctx.shadowOffsetX = shadowOffset;
            ctx.shadowOffsetY = shadowOffset;
            
            ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.fillRect(0, canvas.height * 0.8, canvas.width, canvas.height * 0.2);
            ctx.restore();
          }

          resolve(canvas.toDataURL('image/png'));
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const createProductGrid = async (
    images: Array<{ id: string; preview: string }>,
    layout: '2x2' | '3x3' | '4x4'
  ): Promise<string> => {
    const gridSize = layout === '2x2' ? 2 : layout === '3x3' ? 3 : 4;
    const imageSize = 512;
    const padding = 20;
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    canvas.width = gridSize * imageSize + (gridSize + 1) * padding;
    canvas.height = gridSize * imageSize + (gridSize + 1) * padding;
    
    // White background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const loadedImages = await Promise.all(
      images.slice(0, gridSize * gridSize).map(
        (img) =>
          new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = 'anonymous';
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = img.preview;
          })
      )
    );

    loadedImages.forEach((img, index) => {
      const row = Math.floor(index / gridSize);
      const col = index % gridSize;
      const x = col * imageSize + (col + 1) * padding;
      const y = row * imageSize + (row + 1) * padding;
      
      ctx.drawImage(img, x, y, imageSize, imageSize);
    });

    return canvas.toDataURL('image/png');
  };

  const processImages = async (
    images: Array<{ id: string; preview: string }>,
    options: ProductPhotoOptions
  ) => {
    setIsProcessing(true);
    setProgress(0);
    const results: ProcessedProduct[] = [];

    // If grid layout, create single grid image
    if (options.gridLayout && options.gridLayout !== 'single') {
      try {
        const gridImage = await createProductGrid(images, options.gridLayout);
        results.push({
          id: 'grid',
          original: images[0].preview,
          processed: gridImage,
          settings: options,
        });
      } catch (error) {
        console.error('Failed to create grid:', error);
      }
    } else {
      // Process individual images
      for (let i = 0; i < images.length; i++) {
        try {
          const processed = await processProduct(images[i].preview, images[i].id, options);
          results.push({
            id: images[i].id,
            original: images[i].preview,
            processed,
            settings: options,
          });
        } catch (error) {
          console.error(`Failed to process image ${images[i].id}:`, error);
        }
        setProgress(((i + 1) / images.length) * 100);
      }
    }

    setProcessedProducts(results);
    setIsProcessing(false);
    return results;
  };

  const downloadProcessed = (image: ProcessedProduct, filename: string) => {
    const link = document.createElement('a');
    link.href = image.processed;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = (images: ProcessedProduct[], baseFilename: string) => {
    images.forEach((image, index) => {
      const filename = `${baseFilename}_product_${index + 1}.png`;
      downloadProcessed(image, filename);
    });
  };

  return {
    isProcessing,
    progress,
    processedProducts,
    processImages,
    downloadProcessed,
    downloadAll,
  };
};
